/**
 * Zustand der Seite Verfügbarkeit & Vertrieb: URL, Fenster (Zeitraum des Grundkalenders oder sechs
 * Wochen des Monats), die eine Abfrage des Lesemodells mit 2-s-Nachfragen, der Übertragungsstand und
 * die Verbund-Zuordnung aus der Objektwahl.
 */
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PRIVILEGES } from '@stayon/auth-core';
import { useApi } from '@/shared/api';
import { can, useAuth } from '@/shared/auth';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useObjectScope } from '@/shared/object-scope';
import type { AvailabilitySearch } from './availabilitySearch';
import { addIsoDays, isIsoDate, objectIsoToday } from './calendar';
import { monthOf, monthWindow } from './month/monthModel';
import { summarizeTransmissions, transmissionPollInterval } from './transmissionStatus';

export function useAvailabilityPage() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { group } = useObjectScope();
  const { user } = useAuth();
  const search = useSearch({ strict: false }) as AvailabilitySearch;
  const days = search.days ?? 14;
  const tab = search.tab ?? 'calendar';
  const today = objectIsoToday(group?.timezone);
  const from = isIsoDate(search.from) ? search.from : today;
  // Am Handy ersetzt der Monat den Grundkalender aller Apartments (Board „Verfügbarkeit · Handy“).
  const mobile = useMediaQuery('(max-width: 767px)');
  const fallback = mobile ? group?.apartments[0]?.id : undefined;
  const apartmentId = tab === 'calendar' ? (search.apartment ?? fallback) : undefined;
  const month = search.month ?? monthOf(search.day ?? today);
  const window = apartmentId ? monthWindow(month) : { from, to: addIsoDays(from, days - 1) };
  const calendar = useQuery({
    queryKey: ['availability-distribution', group?.id, window.from, window.to],
    queryFn: () => api.availabilityDistribution.calendar(group!.id, window.from, window.to),
    enabled: Boolean(group),
    staleTime: 30_000,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === group?.id ? previous : undefined,
    refetchInterval: (query) =>
      transmissionPollInterval(query.state.data?.apartments, query.state.status === 'error'),
  });
  // Zimmer → Gesamtwohnung, wie die Objektwahl sie kennt: rückt die Zimmer im Raster ein.
  const wholeFlatOf = useMemo(
    () =>
      new Map(
        (group?.apartments ?? []).flatMap((apartment) =>
          apartment.wholeFlatPropertyId ? [[apartment.id, apartment.wholeFlatPropertyId]] : [],
        ) as [string, string][],
      ),
    [group?.apartments],
  );
  const stale = Boolean(calendar.error);
  const summary = useMemo(
    () => (calendar.data ? { ...summarizeTransmissions(calendar.data.apartments), stale } : null),
    [calendar.data, stale],
  );
  const updateSearch = (patch: Partial<AvailabilitySearch>) =>
    void navigate({
      to: '/availability-distribution',
      search: {
        from: search.from,
        days: search.days,
        tab,
        ...keepMonth(search),
        ...patch,
      } as never,
    });

  return {
    group,
    tab,
    today: calendar.data?.objectToday ?? today,
    from,
    /** Erster sichtbarer Tag – Grundkalender oder erste Monatswoche. */
    windowFrom: window.from,
    to: window.to,
    days,
    apartmentId,
    month,
    preselectedDay: search.day,
    calendar,
    summary,
    wholeFlatOf,
    canEdit: can(user, PRIVILEGES.availabilityDistributionEdit),
    canFull: can(user, PRIVILEGES.availabilityDistributionFull),
    updateSearch,
    invalidate: () =>
      void queryClient.invalidateQueries({ queryKey: ['availability-distribution', group?.id] }),
  };
}

/** Der Monat bleibt beim Navigieren erhalten, ein einmaliger Tag aus einem alten Link nicht. */
function keepMonth(search: AvailabilitySearch): Partial<AvailabilitySearch> {
  return { apartment: search.apartment, month: search.month };
}
